import {
  MIRA_PERIOD_ROLLING_12,
  MIRA_SECTIONS,
  MiraAvailabilityState,
  MiraSection,
  MiraSectionDocument,
} from "@/data/models/Mira";
import {
  MIRA_COMPARISON_BRANCHES,
  MIRA_COMPARISON_GROUP,
  MIRA_COMPARISON_MARKET,
  MIRA_COMPARISON_MEMBERS,
  MIRA_COMPARISON_NONE,
  MiraComparisonMode,
} from "@/utils/MiraComparisonView";
import { isShownMetric } from "@/utils/MiraDataQuality";
import { MiraTile } from "@/utils/MiraDataQualityView";

/**
 * The logic behind the Mira landing page: which section cards it shows, in which order, and what
 * each card's availability ring counts. See `docs/analytics/mira-office-screens.md`.
 */

/**
 * The sections that have a detail screen. A card for any other section is shown but does not
 * open, until its screen exists.
 */
export const MIRA_SECTIONS_WITH_SCREEN: MiraSection[] = [
  MiraSection.DATA_QUALITY,
  MiraSection.HYDRY,
  MiraSection.DEVICES,
  MiraSection.BUSINESS_DEVELOPMENT,
  MiraSection.PROCESS_FLOW,
  MiraSection.INSURANCE,
];

/** Each section's icon, shared by its landing card and its group in the unlock list. */
export const MIRA_SECTION_ICONS: Record<MiraSection, string> = {
  [MiraSection.BUSINESS_DEVELOPMENT]: "mdi-chart-line",
  [MiraSection.PROCESS_FLOW]: "mdi-timeline-outline",
  [MiraSection.HYDRY]: "mdi-water",
  [MiraSection.DEVICES]: "mdi-power-plug",
  [MiraSection.INSURANCE]: "mdi-shield-check-outline",
  [MiraSection.DATA_QUALITY]: "mdi-clipboard-check-outline",
};

/**
 * How many figures a landing card shows (Hans, #2610102: six, raised from four). By default its
 * screen's first tiles, in the screen's order (Taimoor, 2026-10-01), so a card never disagrees with
 * its screen; a user may pick and order them in the card's settings.
 */
export const MIRA_LANDING_FIGURES = 6;

/**
 * A landing card's figures (#2610102): without a choice, the screen's first
 * {@link MIRA_LANDING_FIGURES} tiles in its own order; with one, only the chosen figures, in the order
 * they were picked. A user who wants three sees three, not three filled up to six (Hans, 2026-10-30).
 *
 * A chosen figure without a value in the period is skipped. When none of the chosen ones has a value,
 * the card falls back to the default rather than standing empty. Every Mira figure is a figure of the
 * period ("Mira is a look back", #2610040), so there are no snapshot tiles to leave out here.
 *
 * @param tiles - the section's period tiles, in its screen's order.
 * @param chosenIds - the user's chosen metric ids, in order; absent or empty for the default.
 */
export function landingFigures(tiles: MiraTile[], chosenIds: string[] = []): MiraTile[] {
  const chosen = chosenIds
    .map((id) => tiles.find((tile) => tile.id === id))
    .filter((tile): tile is MiraTile => tile !== undefined);

  return (chosen.length > 0 ? chosen : tiles).slice(0, MIRA_LANDING_FIGURES);
}

/**
 * One click in the settings' click sequence (#2610102): an unchosen figure is added as the next
 * position, unless {@link max} are already chosen; a chosen one is taken out, and the ones after it
 * move up a position. Returns a new list, never the one passed in.
 */
export function toggleFigureChoice(choice: string[], id: string, max: number = MIRA_LANDING_FIGURES): string[] {
  if (choice.includes(id)) {
    return choice.filter((chosenId) => chosenId !== id);
  }
  return choice.length < max ? [...choice, id] : [...choice];
}

/** What a section's availability ring counts, for one period. */
export interface MiraSectionAvailability {
  /** Figures the section publishes. */
  available: number;
  /** Figures hidden because the company has too little data for them: the ones it can unlock. */
  waiting: number;
}

/**
 * How many of a section's figures are available, and how many wait on the company's data.
 *
 * **Only `AVAILABLE` and `INSUFFICIENT_DATA` are counted**, by the same rule as the unlock list
 * (plan §5.7): a figure withheld by a decision (`SUPPRESSED`), one our pipeline has not measured
 * (`UNMEASURED`) or one that cannot apply to the company (`NOT_APPLICABLE`) is neither the company's
 * to unlock nor something it has, so counting it would misstate both halves of the bar.
 */
export function sectionAvailability(document: MiraSectionDocument, period: string): MiraSectionAvailability {
  // A retired "Stand heute" figure an older document still carries, and a figure no screen shows
  // (`MIRA_HIDDEN_METRIC_IDS`), are not something the company has or can unlock: not counted here.
  const metrics = document.periods[period]?.metrics ?? {};
  const states = Object.keys(metrics)
    .filter((id) => isShownMetric(id))
    .map((id) => metrics[id].state);

  return {
    available: states.filter((state) => state === MiraAvailabilityState.AVAILABLE).length,
    waiting: states.filter((state) => state === MiraAvailabilityState.INSUFFICIENT_DATA).length,
  };
}

/**
 * What a landing card and its settings count (Taimoor, 2026-10-30): the figures that can go on the
 * card, which are the screen's tiles, and the figures still waiting on the company's data. Not
 * {@link sectionAvailability}'s published figures: those include the ones a screen draws as cards
 * (Abschlussarten, Schadenursachen, Leistungen), so Geschäftsentwicklung read "9/9" above a card and
 * a settings list of three.
 *
 * @param tiles - the section's tiles for the period, as its screen builds them.
 */
export function cardAvailability(tiles: MiraTile[], document: MiraSectionDocument, period: string): MiraSectionAvailability {
  return { available: tiles.length, waiting: sectionAvailability(document, period).waiting };
}

/**
 * The period a page shows: the shared choice (`MiraPeriodModule`) where the page offers it, else its
 * first period, without overwriting the choice (#2610137), as the comparison falls back to "Kein
 * Vergleich" where a screen does not offer it.
 *
 * @param stored - the period chosen last, on this page or another.
 * @param periods - the periods this page offers, in the picker's order.
 */
export function effectivePeriod(stored: string, periods: string[]): string {
  return periods.includes(stored) ? stored : periods[0] ?? MIRA_PERIOD_ROLLING_12;
}

/** What the landing page knows about the comparisons the screens can offer (#2610137). */
export interface MiraLandingComparisonFacts {
  /** IRES's "Alle Mandanten": no comparison at all (#2610111). */
  allClients: boolean;
  groupMaster: boolean;
  /** A section screen has a branch document (never on a group master). */
  branches: boolean;
  /** A member's group view has a value for a figure of some section in the period. */
  groupView: boolean;
  /** The Markt has a value for a figure of some section in the period. */
  market: boolean;
}

/**
 * The comparisons the landing page's Vergleich icon offers (#2610137): any a section screen can offer, in
 * the screens' order (`MiraScreenBase.comparisonOptions`). The choice is the screens' shared one; a screen
 * that does not offer it shows "Kein Vergleich" there.
 */
export function landingComparisonOptions(facts: MiraLandingComparisonFacts): MiraComparisonMode[] {
  const options: MiraComparisonMode[] = [MIRA_COMPARISON_NONE];
  if (facts.allClients) {
    return options;
  }
  if (facts.groupMaster) {
    options.push(MIRA_COMPARISON_MEMBERS);
  } else if (facts.branches) {
    options.push(MIRA_COMPARISON_BRANCHES);
  }
  if (facts.groupView && !facts.groupMaster) {
    options.push(MIRA_COMPARISON_GROUP);
  }
  if (facts.market) {
    options.push(MIRA_COMPARISON_MARKET);
  }
  return options;
}

/**
 * The sections the landing page shows a card for, in order.
 *
 * **The ones with a detail screen first**, then Mira's section order, so the card that opens is the
 * first thing on the page. **A section with nothing to count in the period gets no card**: no figure
 * available and none waiting on the company's data (plan §5.7, "a section with no available metric
 * hides itself", kept for sections whose figures wait on data, since that is what the company can
 * change). That covers a section the job wrote no document for, one that cannot apply to the company
 * (`NOT_APPLICABLE`), and one whose figures are all withheld by a decision or were published only for
 * older periods, as for a client whose HyDry dryings are older than the last 12 months.
 *
 * @param sections - the section documents that exist.
 * @param period - the period the landing page shows; the last 12 months.
 */
export function landingSections(
  sections: Partial<Record<MiraSection, MiraSectionDocument>>,
  period: string = MIRA_PERIOD_ROLLING_12
): MiraSection[] {
  const shown = MIRA_SECTIONS.filter((section) => {
    const document = sections[section];
    if (!document) {
      return false;
    }

    const { available, waiting } = sectionAvailability(document, period);
    return available + waiting > 0;
  });

  return [
    ...shown.filter((section) => MIRA_SECTIONS_WITH_SCREEN.includes(section)),
    ...shown.filter((section) => !MIRA_SECTIONS_WITH_SCREEN.includes(section)),
  ];
}

/** One arc of a ring, as SVG `stroke-dasharray` / `stroke-dashoffset` values. */
export interface MiraDonutArc {
  key: string;
  /** The arc's length along the circumference, gap already taken off. */
  length: number;
  /** How far along the circumference the arc starts, from the top. */
  offset: number;
}

/**
 * The arcs of a ring: each part in order, clockwise from the top, sized by its share of the total.
 *
 * Parts that meet get a surface-coloured gap at each join (`gap`, in the same unit as the
 * circumference), which is what separates them instead of a drawn border. An empty part draws no arc,
 * a ring with one part left is a full circle with no gap, and a ring with nothing to count draws no arc
 * at all, only its track. Used by the landing page's availability ring and by the meter readings.
 *
 * @param segments - the parts, in drawing order.
 * @param circumference - the ring's circumference.
 * @param gap - the width of each join.
 */
export function donutArcs(
  segments: Array<{ key: string; value: number }>,
  circumference: number,
  gap: number
): MiraDonutArc[] {
  const parts = segments.filter((segment) => segment.value > 0);
  const total = parts.reduce((sum, part) => sum + part.value, 0);
  if (total === 0) {
    return [];
  }

  const joinGap = parts.length > 1 ? gap : 0;

  let offset = 0;
  return parts.map((part) => {
    const span = (circumference * part.value) / total;
    const arc = { key: part.key, length: Math.max(0, span - joinGap), offset };
    offset += span;
    return arc;
  });
}
